import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { formatDateTime, apiErrorMessage } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Webhook, 
  Plus, 
  Trash2, 
  Edit, 
  Play, 
  Eye, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  RefreshCw,
  Copy,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../lib/logger';

const WebhookSettings = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    events: [],
    secret: '',
    headers: {},
    is_active: true
  });
  const [headerKey, setHeaderKey] = useState('');
  const [headerValue, setHeaderValue] = useState('');

  useEffect(() => {
    fetchWebhooks();
    fetchEvents();
  }, []);

  const fetchWebhooks = async () => {
    try {
      const response = await api.get('/webhooks');
      setWebhooks(response.data);
    } catch (error) {
      toast.error('Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get('/webhook-events');
      setEvents(response.data);
    } catch (error) {
      logger.error('Failed to load events');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedWebhook) {
        await api.put(`/webhooks/${selectedWebhook.id}`, formData);
        toast.success('Webhook updated successfully');
      } else {
        await api.post('/webhooks', formData);
        toast.success('Webhook created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchWebhooks();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save webhook'));
    }
  };

  const handleDelete = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) return;
    try {
      await api.delete(`/webhooks/${webhookId}`);
      toast.success('Webhook deleted successfully');
      fetchWebhooks();
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const handleTest = async (webhookId) => {
    setTestResult(null);
    try {
      const response = await api.post(`/webhooks/${webhookId}/test`);
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('Test webhook delivered successfully');
      } else {
        toast.error(`Test failed: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('Failed to send test webhook');
    }
  };

  const handleViewLogs = async (webhook) => {
    try {
      const response = await api.get(`/webhooks/${webhook.id}/logs`);
      setWebhookLogs(response.data);
      setSelectedWebhook(webhook);
      setLogsDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load webhook logs');
    }
  };

  const handleEdit = (webhook) => {
    setSelectedWebhook(webhook);
    setFormData({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events,
      secret: webhook.secret || '',
      headers: webhook.headers || {},
      is_active: webhook.is_active
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedWebhook(null);
    setFormData({
      name: '',
      url: '',
      events: [],
      secret: '',
      headers: {},
      is_active: true
    });
    setHeaderKey('');
    setHeaderValue('');
  };

  const toggleEvent = (eventValue) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(eventValue)
        ? prev.events.filter(e => e !== eventValue)
        : [...prev.events, eventValue]
    }));
  };

  const addHeader = () => {
    if (headerKey && headerValue) {
      setFormData(prev => ({
        ...prev,
        headers: { ...prev.headers, [headerKey]: headerValue }
      }));
      setHeaderKey('');
      setHeaderValue('');
    }
  };

  const removeHeader = (key) => {
    setFormData(prev => {
      const newHeaders = { ...prev.headers };
      delete newHeaders[key];
      return { ...prev, headers: newHeaders };
    });
  };

  const generateSecret = () => {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    const secret = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    setFormData(prev => ({ ...prev, secret }));
    toast.success('Secret generated');
  };

  const copySecret = () => {
    navigator.clipboard.writeText(formData.secret);
    toast.success('Secret copied to clipboard');
  };

  const getStatusBadge = (webhook) => {
    if (webhook.status === 'failed') {
      return <Badge className="bg-red-100 text-red-700">Failed</Badge>;
    }
    if (!webhook.is_active) {
      return <Badge className="bg-slate-100 text-slate-700">Inactive</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>;
  };

  const getLogStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-amber-500" />;
    }
  };

  // Group events by category
  const eventCategories = {
    'Patient Events': events.filter(e => e.value.startsWith('patient.')),
    'Clinical Events': events.filter(e => 
      e.value.startsWith('prescription.') || 
      e.value.startsWith('lab_test.') || 
      e.value.startsWith('imaging.') ||
      e.value.startsWith('procedure.')
    ),
    'Billing Events': events.filter(e => 
      e.value.startsWith('bill.') || 
      e.value.startsWith('payment.')
    ),
    'Appointment Events': events.filter(e => e.value.startsWith('appointment.'))
  };

  return (
    <div data-testid="webhook-settings-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Webhook Integrations</h1>
            <p className="text-slate-500">Configure webhooks to send events to external systems</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-red-500 hover:bg-red-600 rounded-full" data-testid="add-webhook-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Webhook
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-[Manrope]">
                  {selectedWebhook ? 'Edit Webhook' : 'Add New Webhook'}
                </DialogTitle>
                <DialogDescription>
                  Configure a webhook to receive real-time events from your hospital system.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label>Webhook Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Patient Notifications"
                    required
                    data-testid="webhook-name"
                  />
                </div>

                <div>
                  <Label>Endpoint URL *</Label>
                  <Input
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="https://your-server.com/webhook"
                    type="url"
                    required
                    data-testid="webhook-url"
                  />
                </div>

                <div>
                  <Label>Signing Secret (optional)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.secret}
                      onChange={(e) => setFormData({ ...formData, secret: e.target.value })}
                      placeholder="Used to verify webhook signatures"
                      className="font-mono text-sm"
                    />
                    <Button type="button" variant="outline" onClick={generateSecret}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    {formData.secret && (
                      <Button type="button" variant="outline" onClick={copySecret}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Webhooks include X-HMC-Buddy-Signature header for verification
                  </p>
                </div>

                <div>
                  <Label className="mb-3 block">Events to Subscribe *</Label>
                  <div className="space-y-4 border rounded-lg p-4 max-h-60 overflow-y-auto">
                    {Object.entries(eventCategories).map(([category, categoryEvents]) => (
                      <div key={category}>
                        <p className="font-medium text-sm text-slate-700 mb-2">{category}</p>
                        <div className="grid grid-cols-2 gap-2 ml-2">
                          {categoryEvents.map((event) => (
                            <label key={event.value} className="flex items-center space-x-2 cursor-pointer">
                              <Checkbox
                                checked={formData.events.includes(event.value)}
                                onCheckedChange={() => toggleEvent(event.value)}
                              />
                              <span className="text-sm">{event.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Custom Headers (optional)</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={headerKey}
                      onChange={(e) => setHeaderKey(e.target.value)}
                      placeholder="Header name"
                      className="flex-1"
                    />
                    <Input
                      value={headerValue}
                      onChange={(e) => setHeaderValue(e.target.value)}
                      placeholder="Header value"
                      className="flex-1"
                    />
                    <Button type="button" variant="outline" onClick={addHeader}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {Object.entries(formData.headers).length > 0 && (
                    <div className="space-y-1">
                      {Object.entries(formData.headers).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between bg-slate-50 px-2 py-1 rounded text-sm">
                          <span><code>{key}</code>: {value}</span>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeHeader(key)}>
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    id="is_active"
                  />
                  <Label htmlFor="is_active">Webhook Active</Label>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="save-webhook">
                    {selectedWebhook ? 'Update Webhook' : 'Create Webhook'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="page-content">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
              <Webhook className="w-5 h-5 text-red-500" />
              Configured Webhooks
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading webhooks...</div>
            ) : webhooks.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Webhook className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No webhooks configured.</p>
                <p className="text-sm">Add a webhook to receive real-time events.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Events</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Triggered</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {webhooks.map((webhook) => (
                    <TableRow key={webhook.id} data-testid={`webhook-row-${webhook.id}`}>
                      <TableCell className="font-medium">{webhook.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-xs truncate max-w-xs">{webhook.url}</span>
                          <a href={webhook.url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-600">
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{webhook.events?.length || 0} events</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(webhook)}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {webhook.last_triggered_at ? formatDateTime(webhook.last_triggered_at) : 'Never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleTest(webhook.id)} title="Send Test">
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleViewLogs(webhook)} title="View Logs">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(webhook)} title="Edit">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(webhook.id)} title="Delete">
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Webhook Payload Format</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs overflow-x-auto">
{`{
  "event": "patient.registered",
  "timestamp": "2026-01-20T10:30:00Z",
  "webhook_id": "webhook-uuid",
  "data": {
    "id": "patient-uuid",
    "patient_id": "PT2026012001",
    "full_name": "John Doe",
    ...
  }
}`}
            </pre>
            <div className="mt-4 space-y-2 text-sm text-slate-600">
              <p><strong>Headers included:</strong></p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li><code className="bg-slate-100 px-1 rounded">X-HMC-Buddy-Event</code> - Event type</li>
                <li><code className="bg-slate-100 px-1 rounded">X-HMC-Buddy-Timestamp</code> - Event timestamp</li>
                <li><code className="bg-slate-100 px-1 rounded">X-HMC-Buddy-Webhook-ID</code> - Webhook ID</li>
                <li><code className="bg-slate-100 px-1 rounded">X-HMC-Buddy-Signature</code> - HMAC-SHA256 signature (if secret configured)</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logs Dialog */}
      <Dialog open={logsDialogOpen} onOpenChange={setLogsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              Webhook Logs: {selectedWebhook?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {webhookLogs.length === 0 ? (
              <div className="text-center text-slate-500 py-8">No delivery logs yet.</div>
            ) : (
              <div className="space-y-3">
                {webhookLogs.map((log) => (
                  <div key={log.id} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getLogStatusIcon(log.status)}
                        <span className="font-medium text-sm">{log.event}</span>
                        <Badge variant="outline" className="text-xs">
                          {log.response_status || 'N/A'}
                        </Badge>
                      </div>
                      <span className="text-xs text-slate-500">
                        {formatDateTime(log.created_at)}
                        {log.duration_ms && ` • ${log.duration_ms}ms`}
                      </span>
                    </div>
                    {log.error_message && (
                      <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                        {log.error_message}
                      </div>
                    )}
                    {log.response_body && (
                      <details className="mt-2">
                        <summary className="text-xs text-slate-500 cursor-pointer">Response body</summary>
                        <pre className="mt-1 text-xs bg-slate-50 p-2 rounded overflow-x-auto">
                          {log.response_body}
                        </pre>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WebhookSettings;
